import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { type Address, erc20Abi, formatUnits } from 'viem';
import { useBalance, useReadContracts } from 'wagmi';

import {
    CustodialPanel,
    useCustodialBalance,
    useCustodialDeposit,
    useCustodialJobs,
    useCustodialPanelState,
    useCustodialWalletRequirement,
    useCustodialWithdrawManager,
    useDirectDeposit,
    useDirectWithdraw,
    isSnappyOnboarding,
    useSnappyWalletState,
    allAnchorSources,
    ANCHOR_DEPOSIT_TOKENS,
    type AnchorBalances,
    useBridgeCatalog,
} from '@/features/custodial';
import { useOnboardingLauncher } from '@/features/onboarding';
import { useAnchorChain, useWallet } from '@/features/wallet';
import { USDC_DECIMALS } from '@/lib/api/speed';
import { queryKeys } from '@/lib/query';

import { useAgentReactivation } from '../hooks/useAgentReactivation';
import {
    selectOpenPositionCount,
    selectIsRealMode,
    useTradingStore,
} from '../store';

/**
 * Wires the CustodialPanel to the new backend deposit/withdraw saga hooks.
 * Deposits bridge USDC from the user's Cronos EOA via Relay; withdrawals return
 * USDC to that EOA. The EOA's Cronos USDC balance is read on-chain purely to
 * drive the deposit form's MAX button / over-balance validation.
 */
const CustodialPanelContainer: React.FC = () => {
    const isRealMode = useTradingStore(selectIsRealMode);
    // synced from useAgentReactivation in Trading.tsx — read the store rather than calling the
    // hook again, which would mount a second agent-status query for the same answer.
    const isReactivating = useTradingStore((s) => s.isReactivating);
    const openPositionCount = useTradingStore(selectOpenPositionCount);

    const queryClient = useQueryClient();
    const { address } = useWallet();
    const { anchorChainId } = useAnchorChain();

    const custodialPanel = useCustodialPanelState();
    const custodialBalance = useCustodialBalance();
    const snappy = useSnappyWalletState();
    const launchOnboarding = useOnboardingLauncher((s) => s.launch);
    const admissionRequired = useOnboardingLauncher((s) => s.admissionRequired);

    // Every stable on BOTH anchor chains is read (Cronos has two USDCs,
    // native + bridged USDC.e; Robinhood has USDG), so the deposit form can
    // offer any funded origin regardless of which chain the wallet sits on.
    const anchorSources = useMemo(
        () => allAnchorSources(anchorChainId),
        [anchorChainId],
    );
    const { data: eoaRaw, refetch: refetchEoaBalance } = useReadContracts({
        contracts: anchorSources.map((source) => ({
            address: ANCHOR_DEPOSIT_TOKENS[source].address as Address,
            abi: erc20Abi,
            functionName: 'balanceOf' as const,
            args: address ? [address as Address] : undefined,
            chainId: ANCHOR_DEPOSIT_TOKENS[source].chainId,
        })),
        allowFailure: true,
        query: { enabled: isRealMode && !!address, staleTime: 30_000 },
    });
    // the wallet's NATIVE balance on each anchor: deBridge's fixed fee is paid in it on a
    // wallet-signed deposit, and the form says up front when it cannot be covered.
    const cronosNative = useBalance({
        address: address as Address | undefined,
        chainId: 25,
        query: { enabled: isRealMode && !!address, staleTime: 30_000 },
    });
    const robinhoodNative = useBalance({
        address: address as Address | undefined,
        chainId: 4663,
        query: { enabled: isRealMode && !!address, staleTime: 30_000 },
    });
    // the bridge registry behind the testing switch: read once, and only once the panel is open
    const bridgeCatalog = useBridgeCatalog(custodialPanel.isOpen);
    const anchorNativeBalances = useMemo(
        () => ({
            25: cronosNative.data
                ? parseFloat(
                      formatUnits(
                          cronosNative.data.value,
                          cronosNative.data.decimals,
                      ),
                  )
                : null,
            4663: robinhoodNative.data
                ? parseFloat(
                      formatUnits(
                          robinhoodNative.data.value,
                          robinhoodNative.data.decimals,
                      ),
                  )
                : null,
        }),
        [cronosNative.data, robinhoodNative.data],
    );
    const anchorBalances = useMemo<AnchorBalances>(() => {
        const toNumber = (raw: unknown) =>
            typeof raw === 'bigint'
                ? parseFloat(formatUnits(raw, USDC_DECIMALS))
                : null;
        return Object.fromEntries(
            anchorSources.map((source, index) => [
                source,
                toNumber(eoaRaw?.[index]?.result),
            ]),
        );
    }, [anchorSources, eoaRaw]);

    // Runs when a deposit or withdrawal REACHES A TERMINAL STATE — success or failure alike. A
    // failed deposit can still have debited the wallet (a stuck bridge, a funding leg that never
    // ran), so refreshing only on success left the panel showing pre-deposit figures precisely
    // when they were wrong.
    // set below, once useDirectDeposit exists — onSuccess is passed INTO it.
    const refreshDirectDepositRef = useRef<() => void>(() => undefined);

    // memoized: the operation hooks below thread this into their own useCallbacks, and one of
    // those (the withdraw state machine's job sync) is wired into an effect. A fresh closure per
    // render would re-run that effect on every render for no reason.
    const refetchUntilSettled = custodialBalance.refetchUntilSettled;
    const onSuccess = useCallback(() => {
        refetchUntilSettled();
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.balance(),
        });
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.jobs(),
        });
        // the first deposit is what creates the backend wallet row, and the agent
        // status read is the other signal "One deposit away" keys on: it has a
        // 30s stale time, no polling and refetches only on window focus, so
        // without this the banner outlived the confirmed deposit until the tab
        // was refocused.
        void queryClient.invalidateQueries({
            queryKey: queryKeys.speed.agentStatus(),
        });
        // the "Wallet: $X" figure in the deposit form is the user's ORIGIN-CHAIN balance, read
        // through wagmi under its own query key with a 30s staleTime — the invalidations above do
        // not touch it, so a completed deposit left it showing the old number.
        void refetchEoaBalance();
        // the HyperEVM-side reads (EOA USDC on 999, HYPE gas, route) live in useDirectDeposit
        // under their own keys — a completed operation moved all three.
        refreshDirectDepositRef.current();
    }, [queryClient, refetchEoaBalance, refetchUntilSettled]);

    // a settled deposit on a never-activated wallet continues straight into the activation (one
    // signature prompt, no announcement): the first deposit IS the setup. The hook itself decides
    // whether there is anything to activate and waits for the balance read to show the funds.
    const { armFirstActivation } = useAgentReactivation();
    const onDepositSuccess = useCallback(() => {
        onSuccess();
        armFirstActivation();
    }, [armFirstActivation, onSuccess]);

    const custodialDeposit = useCustodialDeposit({
        onSuccess: onDepositSuccess,
        heldOnHyperEvm: () => custodialBalance.custodialUsdc,
    });
    const custodialWithdrawMgr = useCustodialWithdrawManager({ onSuccess });
    // EOA-direct HyperEVM withdrawal — the user's own wallet signs on chain 999; no backend job.
    const directWithdraw = useDirectWithdraw({ onSuccess });
    // ...and its mirror image: deposits that ORIGINATE on HyperEVM.
    const directDeposit = useDirectDeposit({ onSuccess: onDepositSuccess });

    useEffect(() => {
        refreshDirectDepositRef.current = directDeposit.refresh;
    }, [directDeposit.refresh]);

    // HyperCore charges a one-time activation fee on the first funding of an account. The
    // adapter existing with nothing in EITHER HyperCore bucket is that state.
    const directDepositControls = {
        ...directDeposit,
        // ...but only once the balance is actually known — a null is "not read yet", and
        // enforcing the first-funding floor on it would block a legitimate small top-up.
        // an open position also exempts: fully-consumed margin reads as zero withdrawable,
        // yet the position itself proves the account was activated long ago.
        //
        // ANY market's position proves that, not just the charted one. This read
        // `!activeTradeVisual` — the projection — so holding ETH while looking at
        // BTC re-applied the first-funding floor to an account that was plainly
        // activated.
        activationPending:
            directDeposit.route === 'selfFund' &&
            openPositionCount === 0 &&
            custodialBalance.adapterUsdc === 0 &&
            custodialBalance.hyperCoreWithdrawable === 0,
    };
    const custodialJobs = useCustodialJobs(isRealMode);

    // deposits leave from the owner EOA and withdrawals land on it: a passkey
    // or social session opening this panel is sent through the Connected
    // Wallet window (with the reason) and comes back on the same tab.
    useCustodialWalletRequirement();

    // the panel's ONE refresh button. Users read "refresh" as "show me the current state of
    // this dialog", so it covers every figure on it: the wallet balance, the operations list,
    // the Cronos deposit sources (USDC + USDC.e), and the HyperEVM-side reads on both tabs
    // (EOA USDC, HYPE gas, route). Per-section buttons were the old shape and confused people.
    const refetchBalance = custodialBalance.refetch;
    const refetchJobs = custodialJobs.refetch;
    const refreshDirectDeposit = directDeposit.refresh;
    const refreshAll = useCallback(() => {
        refetchBalance();
        void refetchJobs();
        void refetchEoaBalance();
        refreshDirectDeposit();
        void queryClient.invalidateQueries({
            queryKey: ['speed', 'directWithdraw'],
        });
    }, [
        queryClient,
        refetchBalance,
        refetchEoaBalance,
        refetchJobs,
        refreshDirectDeposit,
    ]);
    const autoOpenedJobRef = useRef<string | null>(null);
    const autoAttachedJobRef = useRef<string | null>(null);

    // Terminal jobs never appear in `activeJobs`, so the attach effect below cannot see one:
    // a withdrawal that ends `failed` (HyperCore refusing the exit sweep is exactly that
    // shape) used to leave the panel showing "Moving funds to the bridge wallet" with no way
    // out. Feed every job we know about to the withdraw state machine, which ignores the ones
    // it is not tracking and settles the one it is.
    const syncWithdrawFromJob = custodialWithdrawMgr.syncFromJob;
    useEffect(() => {
        for (const job of custodialJobs.jobs) {
            syncWithdrawFromJob(job);
        }
    }, [custodialJobs.jobs, syncWithdrawFromJob]);

    useEffect(() => {
        // only the panel's own two operations: a `migrate` job (V1 to V2) is
        // followed by the migration dialog and must neither open this panel
        // nor be attached to the withdraw state machine; agent setup has its
        // own progress.
        const activeJob = custodialJobs.activeJobs.find(
            (job) => job.kind === 'deposit' || job.kind === 'withdraw',
        );
        if (!activeJob) {
            autoOpenedJobRef.current = null;
            autoAttachedJobRef.current = null;
            return;
        }

        if (
            !custodialPanel.isOpen &&
            autoOpenedJobRef.current !== activeJob.jobId
        ) {
            autoOpenedJobRef.current = activeJob.jobId;
            custodialPanel.openForJob(
                activeJob.kind === 'withdraw' ? 'withdraw' : 'deposit',
            );
        }

        const attachKey = `${activeJob.jobId}:${activeJob.status}:${activeJob.step}:${activeJob.updatedAt}`;
        if (autoAttachedJobRef.current === attachKey) {
            return;
        }
        autoAttachedJobRef.current = attachKey;
        void (
            activeJob.kind === 'withdraw'
                ? custodialWithdrawMgr.attach(activeJob)
                : custodialDeposit.attach(activeJob)
        ).finally(() => void custodialJobs.refetch());
    }, [
        custodialDeposit,
        custodialJobs,
        custodialJobs.activeJobs,
        custodialPanel,
        custodialPanel.isOpen,
        custodialWithdrawMgr,
    ]);

    if (!isRealMode) return null;

    return (
        <CustodialPanel
            bridgeCatalog={bridgeCatalog.data ?? null}
            open={custodialPanel.isOpen}
            onClose={custodialPanel.close}
            defaultTab={custodialPanel.defaultTab}
            prefillAmount={custodialPanel.prefillAmount ?? undefined}
            custodialBalance={custodialBalance.numericBalance}
            withdrawableBalance={custodialBalance.numericWithdrawable}
            minWithdrawUsd={custodialBalance.minWithdrawUsd}
            flatFeeUsd={custodialBalance.flatFeeUsd}
            anchorBalances={anchorBalances}
            anchorNativeBalances={anchorNativeBalances}
            anchorChainId={anchorChainId}
            isBalanceLoading={custodialBalance.isLoading}
            onRefreshBalance={refreshAll}
            depositState={custodialDeposit.state}
            onDepositConfirm={custodialDeposit.confirm}
            quoteDeposit={custodialDeposit.quoteDeposit}
            onDepositCancel={custodialDeposit.cancel}
            onDepositRetry={custodialDeposit.retry}
            onDepositReset={custodialDeposit.reset}
            onDepositContinue={custodialDeposit.continueActive}
            canContinueDeposit={custodialDeposit.canContinue}
            onDepositCancelActive={custodialDeposit.cancelActive}
            canCancelDeposit={custodialDeposit.canCancel}
            withdrawState={custodialWithdrawMgr.state}
            onWithdraw={custodialWithdrawMgr.withdraw}
            onWithdrawAll={custodialWithdrawMgr.withdrawAll}
            quoteWithdrawal={custodialWithdrawMgr.quoteWithdrawal}
            onWithdrawReset={custodialWithdrawMgr.reset}
            isWithdrawing={custodialWithdrawMgr.isWithdrawing}
            tokenDecimals={USDC_DECIMALS}
            // ANY open market blocks a withdraw, not just the charted one. Reading
            // the projection here failed OPEN: hold BTC, look at ETH, and the
            // withdraw form offered to move collateral that is locked as margin.
            hasActivePosition={openPositionCount > 0}
            isReactivating={isReactivating}
            activationRequired={snappy.status === 'reactivationRequired'}
            directWithdraw={directWithdraw}
            directDeposit={directDepositControls}
            hyperEvmHeldBalance={custodialBalance.custodialUsdc}
            onboardingRequired={isSnappyOnboarding(snappy)}
            walletReady={
                snappy.status === 'firstDepositRequired' &&
                snappy.cohort === 'wallet'
            }
            admissionRequired={admissionRequired}
            onRequestSetup={() =>
                launchOnboarding(
                    custodialDeposit.state.onboardingStep ?? 'wallet',
                )
            }
            jobs={custodialJobs.jobs}
            jobsLoading={custodialJobs.isLoading}
            jobsFetching={custodialJobs.isFetching}
        />
    );
};

export default CustodialPanelContainer;
